import { DocumentDuplicateIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { SettingKey } from 'config'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { useEffect, useState } from 'react'
import { deleteLoanTermSheetTemp, getAdminConfig, getAllLoanTermSheetTemps, setAdminConfig } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Input2, Tooltip } from 'stories/components'
import { confirm } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { DEFAULT_SHEET_TYPE, ILoanTermSheetTemp, SheetTypeOptions } from './constants'
import { LoanTermSheetDetails } from './details'

const channelMap: Record<string, string> = {
  '0': 'Wholesale',
  '1': 'Correspondent',
  '2': 'Retail',
  '3': 'Table Funder',
  '4': 'Brokered',
}

export const LoanTermSheet = () => {
  const { label: productTypeLabel } = useProductTypeOptions()
  const [query, setQuery] = useState('')
  const [templates, setTemplates] = useState<ILoanTermSheetTemp[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDuplicated, setIsDuplicated] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [termSheetSignMessage, setTermSheetSignMessage] = useState('')

  useEffect(() => {
    refresh()
    getAdminConfig(SettingKey.TERM_SHEET_SIGN_MESSAGE).then((data) => setTermSheetSignMessage(data))
  }, [])

  const refresh = () => {
    setIsLoading(true)
    getAllLoanTermSheetTemps()
      .then((data: ILoanTermSheetTemp[]) => {
        setTemplates(data)
      })
      .finally(() => setIsLoading(false))
  }

  if (isEditing)
    return (
      <LoanTermSheetDetails
        lastUpdatedAt={lastUpdatedAt}
        defaults={selectedIndex === -1 ? null : templates[selectedIndex]}
        isDuplicated={isDuplicated}
        allTemplates={templates}
        onBack={() => setIsEditing(false)}
        onComplete={refresh}
      />
    )

  const onAdd = () => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(-1)
    setIsEditing(true)
    setIsDuplicated(false)
  }

  const onEdit = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
    setIsDuplicated(false)
  }

  const onDuplicate = (index: number) => {
    setLastUpdatedAt(Date.now())
    setSelectedIndex(index)
    setIsEditing(true)
    setIsDuplicated(true)
  }

  const getChannelDisplay = (types: Record<string, any>) => {
    return Object.keys(types)
      .filter((key) => !!types[key])
      .map((t) => channelMap[t] || t)
      .join(', ')
  }

  const onDelete = async (index: number) => {
    const template = templates[index]
    const productType =
      productTypeLabel(template.productType) || template.productType
    const transactionType = template.transactionType
    const channel = getChannelDisplay(template.types)

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to remove this template?
        <br />
        <span className="text-gray-600">
          {productType} / {transactionType} / {channel}
        </span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setIsLoading(true)
    deleteLoanTermSheetTemp(template.id)
      .then(() => refresh())
      .catch(() => setIsLoading(false))
  }

  return (
    <div>
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Loan Term Sheet / Pre-Approval Templates
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="flex flex-wrap justify-between mb-3">
        <div className="flex items-center flex-wrap">
          <div className="md:w-96 w-72">
            <Input2
              type="search"
              title="Search"
              hasIcon
              icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />}
              value={query}
              onChange={(value) => setQuery(value)}
            />
          </div>
          <p className="ml-5">- {templates.length} templates</p>
        </div>
        <div className="w-32">
          <Button full onClick={onAdd}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Product Type
              </th>
              <th scope="col" className="px-3 py-3">
                Transaction Type
              </th>
              <th scope="col" className="px-3 py-3">
                Channel
              </th>
              <th scope="col" className="px-3 py-3">
                Sheet Type
              </th>
              <th scope="col" className="px-3 py-3">
                Fields
              </th>
              <th scope="col" className="px-3 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template, index) => {
              const productType =
                productTypeLabel(template.productType) || template.productType
              const transactionType = template.transactionType
              const channel = getChannelDisplay(template.types)
              const sheetTypeKey = template.sheetType || DEFAULT_SHEET_TYPE
              const sheetType = SheetTypeOptions[sheetTypeKey as keyof typeof SheetTypeOptions] || sheetTypeKey

              const searchText = [productType, transactionType, channel, sheetType].join(' ').toLowerCase()
              if (query && !searchText.includes(query.trim().toLowerCase())) return null

              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={template.id}>
                  <td className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{index + 1}</td>
                  <td className="px-3">{productType}</td>
                  <td className="px-3">{transactionType}</td>
                  <td className="px-3">{channel}</td>
                  <td className="px-3">{sheetType}</td>
                  <td className="px-3">{template.fields?.length || 0}</td>
                  <td>
                    <span className="flex px-3">
                      <Tooltip message="Edit">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onEdit(index)}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message="Duplicate">
                        <span
                          className="text-blue-800 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onDuplicate(index)}
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message="Delete">
                        <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(index)}>
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <RenderInput
          input={{
            inputType: 'textarea',
            title: 'Success Message after signed',
            value: termSheetSignMessage,
          }}
          Key="TermSheetSuccessMessage"
          onChange={(key: string, value: string) => setTermSheetSignMessage(value)}
          onBlur={() => setAdminConfig('term_sheet_sign_message', { value: termSheetSignMessage })}
        />
      </div>
    </div>
  )
}
